import { InspectorForm, Metrics } from "../types/inspector.types";
import DateSelect from "../../../../Components/Common/DateSelect/DateSelect";
import Input from "../../../../Components/Common/Input/Input";
import Button from "Components/Common/Button/Button";

interface SubmitTabProps {
  metrics: Metrics;
  inspectorForm: InspectorForm;
  submitted: boolean;
  projectName: string;
  isSaving: boolean;
  isSubmitting: boolean;
  saveError: string;
  submitError: string;
  saveDraftSuccess: boolean;
  onInspectorFormChange: (field: keyof InspectorForm, value: string) => void;
  onSubmit: () => void;
  onSaveDraft: () => void;
  onNewInspection: () => void;
}

export default function SubmitTab({
  metrics,
  inspectorForm,
  submitted,
  projectName,
  isSaving,
  isSubmitting,
  saveError,
  submitError,
  saveDraftSuccess,
  onInspectorFormChange,
  onSubmit,
  onSaveDraft,
  onNewInspection,
}: SubmitTabProps) {
  const busy = isSaving || isSubmitting;
  const today = new Date().toISOString().split("T")[0];

  if (submitted) {
    return (
      <div className="successCard">
        <div className="successScreen">
          <div className="successCircle">✓</div>
          <div className="successTitle">Report Submitted</div>
          <div className="successSub">
            Inspection #{inspectorForm.number} for{" "}
            {projectName || "this project"} has been submitted to CSC New
            England. {metrics.flagsCount} flag
            {metrics.flagsCount !== 1 ? "s" : ""} noted — the progress report
            will be prepared and sent to {inspectorForm.bank}.
          </div>
          <Button className="newInspectionBtn" onClick={onNewInspection}>
            Start New Inspection
          </Button>
        </div>
      </div>
    );
  }

  const allInspected =
    metrics.inspectedCount >= metrics.sovItemsCount &&
    metrics.sovItemsCount > 0;

  return (
    <>
      <div className="submitChecklist">
        <div className="scItem">
          <div className={`scIcon ${allInspected ? "done" : "pending"}`}>
            {allInspected ? "✓" : "!"}
          </div>
          <div className="scText">
            <div className="scLabel">
              {metrics.inspectedCount} / {metrics.sovItemsCount} SOV items
              reviewed
            </div>
            <div className="scSub">FI form line items for this draw</div>
          </div>
        </div>
        <div className="scItem">
          <div
            className={`scIcon ${metrics.photosCount > 0 ? "done" : "pending"}`}
          >
            {metrics.photosCount > 0 ? "✓" : "!"}
          </div>
          <div className="scText">
            <div className="scLabel">{metrics.photosCount} photos captured</div>
            <div className="scSub">Add more if needed before submitting</div>
          </div>
        </div>
        {metrics.flagsCount > 0 && (
          <div className="scItem">
            <div className="scIcon pending">!</div>
            <div className="scText">
              <div className="scLabel">
                {metrics.flagsCount} flag{metrics.flagsCount !== 1 ? "s" : ""}{" "}
                raised
              </div>
              <div className="scSub">
                CSC will address flagged items in the report
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="inspectorFormCard">
        {(
          [
            {
              field: "name" as keyof InspectorForm,
              label: "Inspector Name",
              type: "text",
              readOnly: true,
            },
            {
              field: "number" as keyof InspectorForm,
              label: "Inspection #",
              type: "text",
              readOnly: true,
            },
            {
              field: "date" as keyof InspectorForm,
              label: "Inspection Date",
              type: "date",
              readOnly: true,
            },
            {
              field: "estimate" as keyof InspectorForm,
              label: "Overall CSC Estimate (%)",
              type: "text",
              readOnly: true,
            },
            {
              field: "bank" as keyof InspectorForm,
              label: "Bank",
              type: "text",
              readOnly: true,
            },
          ] as {
            field: keyof InspectorForm;
            label: string;
            type: string;
            readOnly: boolean;
          }[]
        ).map(({ field, label, type, readOnly }) => (
          <div className="inspectorField" key={field}>
            <div className="inspectorLabel">{label}</div>
            {type === "date" ? (
              <DateSelect
                value={today}
                onChange={() => {}}
                disabled={readOnly || busy}
                className="inspectionDate"
              />
            ) : (
              <Input
                className={`inspectorInput${readOnly ? " readOnly" : ""}`}
                type={type}
                value={
                  field === "estimate"
                    ? String(inspectorForm[field] ?? "")
                    : inspectorForm[field]
                }
                onChange={(e) =>
                  !readOnly && onInspectorFormChange(field, e.target.value)
                }
                readOnly={readOnly}
                disabled={!readOnly && busy}
                aria-label={label}
              />
            )}
          </div>
        ))}
      </div>

      {submitError && <div className="submitMsg error">{submitError}</div>}
      {saveError && <div className="submitMsg error">{saveError}</div>}
      {saveDraftSuccess && (
        <div className="submitMsg success">Draft saved successfully.</div>
      )}

      <Button className="submitReportBtn" onClick={onSubmit} disabled={busy}>
        {isSubmitting ? (
          <>
            <div className="btn-spinner" /> Submitting
          </>
        ) : (
          "Submit Inspection Report"
        )}
      </Button>
      <Button className="saveDraftBtn" onClick={onSaveDraft} disabled={busy}>
        {isSaving ? "Saving…" : "Save Draft & Continue Later"}
      </Button>
    </>
  );
}
